import type { ReactNode } from "react";
import type { UseCaseArtworkId } from "./useCaseArtwork";

import React from "react";

import { cn } from "@replohq/lattice/utils/cn";

import { USE_CASE_ARTWORK } from "./useCaseArtwork";

import "./useCaseWorkflowArt.css";

const ridgePage = new URL("./assets/brands/ridge-page.webp", import.meta.url)
  .href;
const im8Page = new URL("./assets/brands/im8-page.webp", import.meta.url).href;
const grazaPage = new URL("./assets/brands/graza-page.webp", import.meta.url)
  .href;
const figmaLogo = new URL("./assets/figma.svg", import.meta.url).href;
const grazaDuo = new URL("./assets/brands/graza-duo.webp", import.meta.url)
  .href;
const coffee = new URL("./assets/coffee-original.webp", import.meta.url).href;
const coffeeBox = new URL("./assets/coffee-box.webp", import.meta.url).href;
const ag1 = new URL("./assets/ag1-ad.webp", import.meta.url).href;
const im8 = new URL("./assets/im8-ad.webp", import.meta.url).href;
const jolie = new URL("./assets/brands/jolie-cutout.webp", import.meta.url)
  .href;
const raincoat = new URL("./assets/raincoat-original.webp", import.meta.url)
  .href;
const yellowbird = new URL("./assets/jalapeno-original.webp", import.meta.url)
  .href;
const sweetgreen = new URL("./assets/brands/sweetgreen.webp", import.meta.url)
  .href;
const heroPatch = new URL("./assets/brands/heroPatch.webp", import.meta.url)
  .href;
const heroShield = new URL("./assets/brands/heroShield.webp", import.meta.url)
  .href;
const jonesRoad = new URL("./assets/brands/jonesRoad.webp", import.meta.url)
  .href;
const native = new URL("./assets/brands/native.webp", import.meta.url).href;
const onRunning = new URL("./assets/brands/onRunning.webp", import.meta.url)
  .href;
const lovevery = new URL("./assets/brands/lovevery.webp", import.meta.url).href;
const liquidIv = new URL("./assets/brands/liquidIv.webp", import.meta.url).href;
const brex = new URL("./assets/brands/brex.webp", import.meta.url).href;
const hiya = new URL("./assets/brands/hiya.webp", import.meta.url).href;
const javvy = new URL("./assets/brands/javvy.webp", import.meta.url).href;
const casper = new URL("./assets/brands/casper.webp", import.meta.url).href;
const armra = new URL("./assets/brands/armra.webp", import.meta.url).href;

function Text({
  x = 0,
  y = 0,
  children,
  kind = "body",
  fill,
}: React.PropsWithChildren<{
  x?: number;
  y?: number;
  kind?: "body" | "title" | "label" | "metric";
  fill?: string;
}>) {
  return (
    <text x={x} y={y} className={cn(`thumbnail-${kind}`)} fill={fill}>
      {children}
    </text>
  );
}

function Panel({
  x,
  y,
  width,
  height,
  children,
  tone = "paper",
}: React.PropsWithChildren<{
  x: number;
  y: number;
  width: number;
  height: number;
  tone?: "paper" | "green" | "rose";
}>) {
  return (
    <g transform={`translate(${x} ${y})`} data-art-piece>
      <rect
        width={width}
        height={height}
        rx="16"
        className={cn(`thumbnail-panel thumbnail-panel-${tone}`)}
      />
      {children}
    </g>
  );
}

function Rule({ y, width, x = 22 }: { y: number; width: number; x?: number }) {
  return <path d={`M${x} ${y}h${width}`} className="thumbnail-rule" />;
}

function Chip({
  x,
  y,
  children,
  tone = "green",
  width = 100,
}: React.PropsWithChildren<{
  x: number;
  y: number;
  tone?: "green" | "rose" | "neutral";
  width?: number;
}>) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect
        width={width}
        height="28"
        rx="14"
        className={cn(`thumbnail-chip-${tone}`)}
      />
      <text
        x={width / 2}
        y="19"
        textAnchor="middle"
        className="thumbnail-label"
      >
        {children}
      </text>
    </g>
  );
}

function Photo({
  x,
  y,
  width,
  height,
  file,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  file: string;
}) {
  const clipId = React.useId();
  const isPhoto = [sweetgreen, brex, casper, armra, raincoat].includes(file);
  return (
    <svg
      x={x}
      y={y}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      data-art-image
    >
      <defs>
        <clipPath id={clipId}>
          <rect width={width} height={height} rx={12} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <image
          href={file}
          width={width}
          height={height}
          preserveAspectRatio={isPhoto ? "xMidYMid slice" : "xMidYMid meet"}
        />
      </g>
    </svg>
  );
}

function Button({
  x = 22,
  y,
  width,
  children,
}: React.PropsWithChildren<{
  x?: number;
  y: number;
  width: number;
}>) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={width} height="34" rx="8" fill="#273a2c" />
      <text
        x={width / 2}
        y="23"
        textAnchor="middle"
        className="thumbnail-body"
        fill="#fffef8"
      >
        {children}
      </text>
    </g>
  );
}

function Page({
  x,
  y,
  width,
  height = 262,
  brand,
  headline,
  file,
  action = "Shop now",
  tone = "paper",
}: {
  x: number;
  y: number;
  width: number;
  height?: number;
  brand: string;
  headline: string;
  file: string;
  action?: string;
  tone?: "paper" | "green" | "rose";
}) {
  return (
    <Panel x={x} y={y} width={width} height={height} tone={tone}>
      <Text x={18} y={29} kind="label">
        {brand}
      </Text>
      <Rule y={42} x={0} width={width} />
      <Photo
        x={18}
        y={52}
        width={width - 36}
        height={height - 155}
        file={file}
      />
      <Text x={18} y={height - 78} kind="title">
        {headline}
      </Text>
      <Button x={18} y={height - 54} width={width - 36}>
        {action}
      </Button>
    </Panel>
  );
}

function PagePreview({
  x,
  y,
  width,
  file,
}: {
  x: number;
  y: number;
  width: number;
  file: string;
}) {
  const clipId = React.useId();
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className="workflow-page-preview">
        <defs>
          <clipPath id={clipId}>
            <rect
              className="workflow-page-clip"
              width={width}
              height={360 - y - 12}
              rx={12}
            />
          </clipPath>
        </defs>
        <image
          href={file}
          width={width}
          height={width * 2}
          clipPath={`url(#${clipId})`}
        />
      </g>
    </g>
  );
}

function Table({
  x = 40,
  y = 40,
  width = 560,
  title,
  columns,
  rows,
  highlight = -1,
}: {
  x?: number;
  y?: number;
  width?: number;
  title: string;
  columns: string[];
  rows: string[][];
  highlight?: number;
}) {
  const firstWidth = width * 0.49;
  const remainingWidth = (width - firstWidth - 28) / (columns.length - 1);
  return (
    <Panel x={x} y={y} width={width} height={106 + rows.length * 44}>
      <Text x={22} y={34} kind="title">
        {title}
      </Text>
      {columns.map((column, index) => (
        <Text
          key={column}
          x={index === 0 ? 22 : firstWidth + (index - 1) * remainingWidth}
          y={74}
          kind="label"
        >
          {column}
        </Text>
      ))}
      <Rule y={87} width={width - 44} />
      {rows.map((row, rowIndex) => (
        <g key={row[0]} transform={`translate(0 ${91 + rowIndex * 44})`}>
          {rowIndex === highlight && (
            <rect x="10" width={width - 20} height="40" rx="5" fill="#e8efd8" />
          )}
          {row.map((value, index) => (
            <Text
              key={`${index}-${value}`}
              x={index === 0 ? 22 : firstWidth + (index - 1) * remainingWidth}
              y={26}
            >
              {value}
            </Text>
          ))}
        </g>
      ))}
    </Panel>
  );
}

function Arrow({ x, y }: { x: number; y: number }) {
  return (
    <g className="workflow-connector" transform={`translate(${x} ${y})`}>
      <circle
        cx="15"
        r="18"
        fill="#faf9f2"
        fillOpacity="0.7"
        stroke="#d0d4c3"
        strokeWidth="1"
      />
      <path
        className="workflow-connector-arrow"
        d="M4 0h22m-8-8 8 8-8 8"
        fill="none"
        stroke="#6a6e65"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

function LineChart({
  x,
  y,
  width = 480,
  height = 100,
}: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {[0, 0.5, 1].map((position) => (
        <path
          key={position}
          d={`M0 ${height * position}h${width}`}
          className="thumbnail-rule"
        />
      ))}
      <svg
        width={width}
        height={height}
        viewBox="0 0 480 100"
        preserveAspectRatio="none"
      >
        <path
          d="M0 92 40 76 80 82 120 54 160 63 200 47 240 52 280 26 320 35 360 14 400 25 440 8 480 2L480 100H0Z"
          fill="#e8efd8"
        />
        <path
          d="M0 92 40 76 80 82 120 54 160 63 200 47 240 52 280 26 320 35 360 14 400 25 440 8 480 2"
          stroke="#628344"
          strokeWidth="3"
          fill="none"
        />
      </svg>
    </g>
  );
}

/** Three plans side by side, the middle one marked with `badge`. */
function PlanCards({
  plans,
  badge,
  unit,
}: {
  plans: [name: string, price: string, note: string, action: string][];
  badge: string;
  unit: string;
}) {
  return plans.map(([name, price, note, action], index) => (
    <Panel
      key={name}
      x={40 + index * 192}
      y={66}
      width={176}
      height={262}
      tone={index === 1 ? "green" : "paper"}
    >
      {index === 1 && (
        <Chip x={18} y={16} width={118}>
          {badge}
        </Chip>
      )}
      <Text x={18} y={78} kind="title">
        {name}
      </Text>
      <Text x={18} y={124} kind="metric">
        {price}
      </Text>
      <Text x={18} y={150} kind="label">
        {unit}
      </Text>
      <Rule y={170} x={18} width={140} />
      <Text x={18} y={196} kind="label">
        {note}
      </Text>
      <Button x={18} y={212} width={140}>
        {action}
      </Button>
    </Panel>
  ));
}

const WORKFLOW_SCENES = {
  "ad-to-lander": () => {
    return (
      <>
        <Panel x={22} y={60} width={190} height={245}>
          <Text x={16} y={28} kind="label">
            GRAZA · Sponsored
          </Text>
          <Photo x={16} y={40} width={158} height={145} file={grazaDuo} />
          <Text x={16} y={208}>
            Meet the Duo.
          </Text>
          <Text x={16} y={231} kind="label">
            Drizzle & Sizzle
          </Text>
        </Panel>
        <Arrow x={229} y={176} />
        <PagePreview x={276} y={26} width={340} file={grazaPage} />
      </>
    );
  },
  "paid-social-funnel": () => {
    return (
      <>
        <Page
          x={16}
          y={44}
          width={184}
          height={258}
          brand="COMETEER / STORY"
          headline="Better coffee."
          file={coffee}
          action="Meet the coffee"
        />
        <Arrow x={208} y={172} />
        <Page
          x={246}
          y={30}
          width={184}
          height={286}
          brand="COMETEER / OFFER"
          headline="Your first box."
          file={coffeeBox}
          action="Pick your roast"
          tone="green"
        />
        <Arrow x={439} y={172} />
        <Panel x={478} y={72} width={146} height={216}>
          <Text x={15} y={30} kind="title">
            Checkout
          </Text>
          <Photo x={25} y={44} width={96} height={78} file={coffeeBox} />
          <Text x={15} y={148}>
            32 cups · $64
          </Text>
          <Button x={12} y={165} width={122}>
            Place order
          </Button>
        </Panel>
      </>
    );
  },
  "competitor-teardown": () => {
    return (
      <>
        <Panel x={24} y={28} width={220} height={292}>
          <Text x={18} y={30} kind="title">
            Offer comparison
          </Text>
          <Photo x={16} y={47} width={86} height={147} file={ag1} />
          <Photo x={116} y={47} width={86} height={147} file={im8} />
          <Text x={20} y={226}>
            AG1
          </Text>
          <Text x={123} y={226}>
            IM8
          </Text>
          <Text x={20} y={253} kind="label">
            Free kit
          </Text>
          <Text x={123} y={253} kind="label">
            All-in-one
          </Text>
        </Panel>
        <Panel x={267} y={53} width={350} height={247}>
          <Text x={22} y={34} kind="title">
            Funnel teardown
          </Text>
          <Chip x={22} y={51} width={138} tone="neutral">
            3 opportunities
          </Chip>
          {[
            "01  Offer visible above the fold",
            "02  Proof beside the buy button",
            "03  Subscription savings upfront",
          ].map((line, index) => (
            <g key={line}>
              <Rule y={98 + index * 47} width={306} />
              <Text x={22} y={127 + index * 47}>
                {line}
              </Text>
            </g>
          ))}
        </Panel>
      </>
    );
  },
  "figma-to-page": () => {
    return (
      <>
        <Panel x={24} y={55} width={236} height={244}>
          <image href={figmaLogo} x={18} y={12} width={18} height={27} />
          <Text x={48} y={30} kind="label">
            Figma / Product page
          </Text>
          <rect
            x="24"
            y="49"
            width="188"
            height="170"
            fill="#f0ede5"
            stroke="#9273cf"
            strokeWidth="2"
          />
          <svg x={26} y={51} width={184} height={166} viewBox="0 0 184 166">
            <image href={ridgePage} width={184} height={368} />
          </svg>
          {[
            { x: 24, y: 49 },
            { x: 212, y: 49 },
            { x: 24, y: 219 },
            { x: 212, y: 219 },
          ].map(({ x, y }) => (
            <rect
              key={`${x}-${y}`}
              x={x - 4}
              y={y - 4}
              width="8"
              height="8"
              fill="white"
              stroke="#9273cf"
            />
          ))}
        </Panel>
        <Arrow x={280} y={176} />
        <PagePreview x={330} y={26} width={286} file={ridgePage} />
      </>
    );
  },
  "bundle-builder": () => {
    return (
      <Panel x={43} y={25} width={554} height={310}>
        <Text x={24} y={35} kind="title">
          HERO / Build your routine
        </Text>
        <Chip x={410} y={15} width={119}>
          Save 15%
        </Chip>
        <Photo x={34} y={58} width={146} height={166} file={heroPatch} />
        <Photo x={195} y={58} width={146} height={166} file={heroShield} />
        <Text x={365} y={100}>
          Patch + Shield
        </Text>
        <Text x={365} y={138} kind="metric">
          $26.35
        </Text>
        <Text x={365} y={165} kind="label">
          Usually $31.00
        </Text>
        <Text x={45} y={249}>
          Mighty Patch
        </Text>
        <Text x={204} y={249}>
          Mighty Shield
        </Text>
        <Button x={24} y={266} width={506}>
          Add bundle to bag
        </Button>
      </Panel>
    );
  },
  "quiz-funnel": () => {
    return (
      <>
        <Panel x={24} y={30} width={355} height={294}>
          <Text x={22} y={31} kind="label">
            JONES ROAD · SHADE FINDER
          </Text>
          <Rule y={47} width={311} />
          <Text x={22} y={82} kind="title">
            What finish feels like you?
          </Text>
          {["Fresh & dewy", "Warm & bronzed", "Soft & natural"].map(
            (answer, index) => (
              <g key={answer} transform={`translate(22 ${105 + index * 53})`}>
                <rect
                  width="311"
                  height="41"
                  rx="8"
                  fill={index === 1 ? "#e8efd8" : "#f1f0e9"}
                  stroke={index === 1 ? "#6b8650" : "#d9dbce"}
                />
                <circle
                  cx="19"
                  cy="20"
                  r="7"
                  fill={index === 1 ? "#6b8650" : "none"}
                  stroke="#89917a"
                />
                <Text x={37} y={26}>
                  {answer}
                </Text>
              </g>
            ),
          )}
        </Panel>
        <Panel x={399} y={71} width={217} height={239} tone="green">
          <Text x={19} y={32} kind="label">
            YOUR MATCH
          </Text>
          <Photo x={24} y={47} width={168} height={125} file={jonesRoad} />
          <Text x={19} y={204} kind="title">
            Your Miracle Balm
          </Text>
        </Panel>
      </>
    );
  },
  "winback-machine": () => {
    return (
      <>
        <Panel x={23} y={61} width={267} height={220}>
          <Text x={22} y={33} kind="title">
            Canceled subscribers
          </Text>
          <Text x={22} y={65} kind="label">
            Last order more than 60 days ago
          </Text>
          {[
            "Alex M.     Still stocked up",
            "Jamie R.   Taking a break",
            "Sam K.     Price",
          ].map((line, index) => (
            <Text key={line} x={22} y={110 + index * 38}>
              {line}
            </Text>
          ))}
        </Panel>
        <Panel x={313} y={25} width={302} height={310} tone="green">
          <Text x={22} y={33} kind="label">
            NATIVE / YOUR RETURN OFFER
          </Text>
          <Text x={22} y={70} kind="title">
            Your daily favorite, 20% off.
          </Text>
          <Photo x={62} y={83} width={180} height={134} file={native} />
          <Text x={22} y={244}>
            Coconut & Vanilla. Back for you.
          </Text>
          <Button y={262} width={258}>
            Come back with ALEX20
          </Button>
        </Panel>
      </>
    );
  },
  "sale-launcher": () => {
    return (
      <>
        <Panel x={24} y={43} width={269} height={271}>
          <Text x={22} y={34} kind="title">
            Weekend sale
          </Text>
          <Chip x={22} y={52}>
            Scheduled
          </Chip>
          <Text x={22} y={117} kind="label">
            DISCOUNT
          </Text>
          <Text x={22} y={146} kind="title">
            20% off · WEEKEND20
          </Text>
          <Rule y={165} width={225} />
          <Text x={22} y={199}>
            Fri, 9:00 AM Publish
          </Text>
          <Text x={22} y={236}>
            Sun, 11:59 PM Revert
          </Text>
        </Panel>
        <Page
          x={316}
          y={26}
          width={300}
          height={308}
          brand="ON / WEEKEND SALE"
          headline="Your next pair awaits."
          file={onRunning}
          action="Shop with WEEKEND20"
          tone="green"
        />
      </>
    );
  },
  "seasonal-promo": () => {
    return (
      <>
        <Panel x={24} y={24} width={370} height={312} tone="green">
          <Text x={22} y={32} kind="label">
            LOVEVERY / HOLIDAY GIFTS
          </Text>
          <Rule y={48} width={326} x={22} />
          <Photo x={24} y={72} width={148} height={164} file={lovevery} />
          <Text x={194} y={96} kind="title">
            A little wonder.
          </Text>
          <Text x={194} y={142}>
            The Looker
          </Text>
          <Text x={194} y={165}>
            Play Kit
          </Text>
          <Text x={194} y={198} kind="label">
            For ages 0–12 weeks
          </Text>
          <Button x={22} y={264} width={326}>
            Explore the gift guide
          </Button>
        </Panel>
        <Panel x={414} y={62} width={202} height={236}>
          <Text x={22} y={34} kind="label">
            CAMPAIGN SCHEDULE
          </Text>
          <Text x={22} y={76} kind="title">
            Nov 27 – Dec 2
          </Text>
          <Text x={22} y={108} kind="label">
            Starts at 9 AM
          </Text>
          <Rule y={134} width={158} x={22} />
          <Text x={22} y={168}>
            Ends automatically
          </Text>
          <Chip x={22} y={184} width={158}>
            Scheduled
          </Chip>
        </Panel>
      </>
    );
  },
  "campaign-pages": () => {
    return (
      <>
        <Panel x={25} y={68} width={245} height={219}>
          <Text x={20} y={32} kind="label">
            IM8 / YOUR DAILY ROUTINE
          </Text>
          <Text x={20} y={69} kind="title">
            One daily ritual.
          </Text>
          <Photo x={30} y={86} width={185} height={84} file={im8} />
          <Text x={20} y={198}>
            Meet the essentials →
          </Text>
        </Panel>
        <Arrow x={283} y={175} />
        <PagePreview x={326} y={26} width={288} file={im8Page} />
      </>
    );
  },
  "slide-out-cart": () => {
    return (
      <>
        <Page
          x={28}
          y={46}
          width={277}
          height={260}
          brand="LIQUID I.V."
          headline="Hydration on hand."
          file={liquidIv}
          action="Find your flavor"
          tone="green"
        />
        <Panel x={326} y={20} width={288} height={321}>
          <Text x={20} y={31} kind="title">
            Your bag (1)
          </Text>
          <Text x={250} y={31}>
            ×
          </Text>
          <Text x={20} y={65} kind="label">
            $9 away from free shipping
          </Text>
          <rect x="20" y="77" width="248" height="5" rx="2" fill="#e0e4d7" />
          <rect x="20" y="77" width="188" height="5" rx="2" fill="#668440" />
          <Photo x={20} y={97} width={66} height={83} file={liquidIv} />
          <Text x={100} y={118}>
            Hydration · 16 pack
          </Text>
          <Text x={100} y={147}>
            − 1 + $21
          </Text>
          <Rule y={196} width={244} />
          <Text x={20} y={224}>
            Subtotal
          </Text>
          <Text x={214} y={224}>
            $21
          </Text>
          <Button x={20} y={248} width={248}>
            Checkout
          </Button>
          <Text x={52} y={306} kind="label">
            Shipping calculated at checkout
          </Text>
        </Panel>
      </>
    );
  },
  "page-multiplier": () => {
    return (
      <>
        <Page
          x={18}
          y={60}
          width={188}
          height={256}
          brand="JOLIE / ATHLETES"
          headline="Rinse. Reset."
          file={jolie}
          action="Shop the filter"
        />
        <Page
          x={226}
          y={27}
          width={188}
          height={289}
          brand="JOLIE / SKINCARE"
          headline="Skin starts here."
          file={jolie}
          action="Shop the filter"
          tone="green"
        />
        <Page
          x={434}
          y={60}
          width={188}
          height={256}
          brand="JOLIE / RENTERS"
          headline="Make it yours."
          file={jolie}
          action="Shop the filter"
          tone="rose"
        />
      </>
    );
  },
  "brand-personas": () => {
    return (
      <>
        <Panel x={27} y={39} width={320} height={281}>
          <Chip x={22} y={19} width={128} tone="neutral">
            Persona 01
          </Chip>
          <Text x={22} y={83} kind="title">
            The everyday optimizer
          </Text>
          <Text x={22} y={121}>
            Buys for: a simpler routine
          </Text>
          <Text x={22} y={155}>
            Hesitates on: ingredient quality
          </Text>
          <Rule y={177} width={276} />
          <Text x={22} y={207} kind="label">
            MESSAGE TO TEST
          </Text>
          <Text x={22} y={237}>
            “One scoop. Morning sorted.”
          </Text>
        </Panel>
        <Panel x={370} y={64} width={244} height={235} tone="green">
          <Text x={20} y={34} kind="title">
            Voice of customer
          </Text>
          <Text x={20} y={80}>
            “I replaced five bottles.”
          </Text>
          <Text x={20} y={111} kind="label">
            Verified review · repeat buyer
          </Text>
          <Rule y={134} width={204} x={20} />
          <Text x={20} y={171} kind="metric">
            38%
          </Text>
          <Text x={20} y={200}>
            mention convenience
          </Text>
        </Panel>
      </>
    );
  },
  "page-audit": () => {
    return (
      <>
        <Page
          x={24}
          y={44}
          width={214}
          height={267}
          brand="STUTTERHEIM"
          headline="Ready for rain."
          file={raincoat}
          action="Explore rainwear"
        />
        <Panel x={262} y={32} width={354} height={289}>
          <Text x={22} y={36} kind="title">
            Conversion audit
          </Text>
          <Chip x={22} y={53} tone="rose" width={155}>
            3 fixes to prioritize
          </Chip>
          {[
            ["01", "Offer hidden below fold", "Move savings into the hero"],
            ["02", "No reviews near CTA", "Add verified customer proof"],
            ["03", "Mobile CTA out of reach", "Add a sticky buy button"],
          ].map(([number, title, detail], index) => (
            <g key={number} transform={`translate(22 ${113 + index * 65})`}>
              <Text kind="label" fill="#8d6243">
                {number}
              </Text>
              <Text x={31}>{title}</Text>
              <Text x={31} y={24} kind="label">
                {detail}
              </Text>
            </g>
          ))}
        </Panel>
      </>
    );
  },
  "pixel-audit": () => {
    return (
      <Table
        title="Pixel audit · checkout journey"
        columns={["Event", "Meta", "GA4"]}
        rows={[
          ["PageView", "Present", "Present"],
          ["AddToCart", "Present", "Missing"],
          ["InitiateCheckout", "Present", "Present"],
          ["Purchase", "Duplicate", "Present"],
        ]}
        highlight={1}
      />
    );
  },
  "launch-checklist": () => {
    return (
      <Panel x={62} y={25} width={516} height={308}>
        <Text x={24} y={36} kind="title">
          Pre-launch check
        </Text>
        <Chip x={358} y={16} width={134}>
          Ready to publish
        </Chip>
        <Rule y={58} width={468} x={24} />
        {[
          "Cart & checkout",
          "Mobile layout",
          "Purchase tracking",
          "Domain & SSL",
          "SEO & accessibility",
        ].map((item, index) => (
          <g key={item} transform={`translate(24 ${94 + index * 42})`}>
            <circle cx="10" cy="-5" r="11" fill="#e1eacb" />
            <path
              d="m5-5 4 4 7-8"
              stroke="#56743b"
              strokeWidth="2"
              fill="none"
            />
            <Text x={34}>{item}</Text>
            <Text x={400} kind="label" fill="#53733c">
              Passed
            </Text>
          </g>
        ))}
      </Panel>
    );
  },
  "discount-leak-audit": () => {
    return (
      <>
        <Table
          y={29}
          title="Discount leakage · last 30 days"
          columns={["Code", "Orders", "Leakage"]}
          rows={[
            ["WELCOME15", "84", "$642"],
            ["CREATOR20", "31", "$286"],
            ["VIP25 + bundle", "12", "$198"],
          ]}
          highlight={2}
        />
        <Panel x={319} y={282} width={280} height={53} tone="rose">
          <Text x={18} y={33}>
            Stacking found on 12 orders
          </Text>
        </Panel>
      </>
    );
  },
  "ad-split-test": () => {
    return (
      <>
        <Panel x={26} y={41} width={280} height={277}>
          <Chip x={20} y={17} width={74} tone="neutral">
            Offer A
          </Chip>
          <Text x={20} y={86} kind="title">
            15% off your first box
          </Text>
          <Text x={20} y={143} kind="metric">
            3.2%
          </Text>
          <Text x={20} y={175} kind="label">
            Conversion · 1,240 sessions
          </Text>
          <rect x="20" y="205" width="234" height="13" rx="4" fill="#e9e9df" />
          <rect x="20" y="205" width="166" height="13" rx="4" fill="#9ba783" />
          <Text x={20} y={252}>
            40 purchases
          </Text>
        </Panel>
        <Panel x={332} y={41} width={280} height={277} tone="green">
          <Chip x={20} y={17} width={74}>
            Offer B
          </Chip>
          <Text x={20} y={86} kind="title">
            Free shipping on your box
          </Text>
          <Text x={20} y={143} kind="metric">
            4.5%
          </Text>
          <Text x={20} y={175} kind="label">
            Conversion · 1,218 sessions
          </Text>
          <rect x="20" y="205" width="234" height="13" rx="4" fill="#d9e3c5" />
          <rect x="20" y="205" width="234" height="13" rx="4" fill="#648349" />
          <Text x={20} y={252}>
            55 purchases · Keep testing
          </Text>
        </Panel>
      </>
    );
  },
  "review-carousel": () => {
    return (
      <>
        <Text x={35} y={55} kind="title" fill="#ebeed4">
          POPPI / Sample customer reviews
        </Text>
        {[
          {
            x: 26,
            y: 82,
            name: "Alex M.",
            lines: ["“My afternoon", "pick-me-up.”"],
          },
          {
            x: 326,
            y: 111,
            name: "Jamie R.",
            lines: ["“A fridge full", "of favorites.”"],
          },
        ].map((review) => (
          <Panel
            key={review.name}
            x={review.x}
            y={review.y}
            width={286}
            height={196}
          >
            <Text x={22} y={37} fill="#638045">
              ★★★★★
            </Text>
            {review.lines.map((line, index) => (
              <Text key={line} x={22} y={82 + index * 28} kind="title">
                {line}
              </Text>
            ))}
            <Text x={22} y={162} kind="label">
              {review.name} · Sample review
            </Text>
          </Panel>
        ))}
        <circle cx="303" cy="334" r="4" fill="#dce5b7" />
        <circle cx="320" cy="334" r="4" fill="#505745" />
        <circle cx="337" cy="334" r="4" fill="#505745" />
      </>
    );
  },
  "case-studies-section": () => {
    return (
      <Panel x={30} y={30} width={580} height={295}>
        <Text x={22} y={34} kind="title">
          Customer stories
        </Text>
        <Chip x={22} y={50} width={94}>
          All stories
        </Chip>
        <Chip x={127} y={50} width={111} tone="neutral">
          Finance
        </Chip>
        <Photo x={22} y={96} width={237} height={167} file={brex} />
        <Text x={281} y={120} kind="label">
          BREX
        </Text>
        <Text x={281} y={158} kind="title">
          From spend to clarity
        </Text>
        <Text x={281} y={191}>
          One team. One place to manage.
        </Text>
        <Text x={281} y={245}>
          Read the customer story →
        </Text>
      </Panel>
    );
  },
  "csv-pages": () => {
    return (
      <>
        <Table
          x={23}
          y={33}
          width={355}
          title="products.csv"
          columns={["Product", "Page"]}
          rows={[
            ["Multivitamin", "Daily essentials"],
            ["Probiotic", "Gut health"],
            ["Bedtime", "Evening routine"],
          ]}
        />
        <Page
          x={403}
          y={64}
          width={212}
          height={263}
          brand="HIYA / PUBLISHED"
          headline="Daily essentials."
          file={hiya}
          action="View vitamins"
          tone="green"
        />
      </>
    );
  },
  "creator-pages": () => {
    return (
      <>
        <Page
          x={24}
          y={32}
          width={286}
          height={294}
          brand="JAVVY / THE ALEX EDIT"
          headline="My morning ritual."
          file={javvy}
          action="Shop with ALEX15"
          tone="green"
        />
        <Page
          x={333}
          y={51}
          width={283}
          height={275}
          brand="JAVVY / JAMIE’S PICKS"
          headline="Coffee, my way."
          file={javvy}
          action="Shop with JAMIE15"
        />
      </>
    );
  },
  "city-pages": () => {
    return (
      <>
        <Panel x={24} y={50} width={248} height={246}>
          <Text x={22} y={34} kind="title">
            Campaign destination
          </Text>
          <Text x={22} y={67} kind="label">
            URL PARAMETER
          </Text>
          <Chip x={22} y={81} width={171} tone="neutral">
            ?city=brooklyn
          </Chip>
          <Rule y={132} width={204} />
          <Text x={22} y={166}>
            Brooklyn
          </Text>
          <Text x={22} y={198}>
            Austin
          </Text>
          <Text x={22} y={230}>
            Los Angeles
          </Text>
        </Panel>
        <Arrow x={286} y={171} />
        <Page
          x={330}
          y={28}
          width={286}
          height={299}
          brand="SWEETGREEN / BROOKLYN"
          headline="Brooklyn, dinner’s on."
          file={sweetgreen}
          action="Find your local Sweetgreen"
          tone="green"
        />
      </>
    );
  },
  "retail-partner-pages": () => {
    return (
      <>
        <Page
          x={25}
          y={39}
          width={281}
          height={282}
          brand="YELLOWBIRD / STOCKISTS"
          headline="Your local hot spot."
          file={yellowbird}
          action="Find a stockist"
        />
        <Page
          x={333}
          y={39}
          width={281}
          height={282}
          brand="YELLOWBIRD / DIRECT"
          headline="Turn up the flavor."
          file={yellowbird}
          action="Explore the hot sauces"
          tone="green"
        />
      </>
    );
  },
  "comparison-pages": () => {
    return (
      <Table
        title="CANOPY / Find your shower filter"
        columns={["Feature", "Canopy", "Standard"]}
        rows={[
          ["Filtration", "Included", "Check specs"],
          ["Filter refills", "Available", "Varies"],
          ["Finish options", "Multiple", "Varies"],
          ["Installation", "At home", "Check guide"],
        ]}
        highlight={0}
      />
    );
  },
  "gsc-keyword-lift": () => {
    return (
      <>
        <Table
          y={24}
          title="WILDLING / Search opportunities"
          columns={["Query", "Impr.", "CTR"]}
          rows={[
            ["gua sha for beginners", "8,240", "1.2%"],
            ["facial massage tools", "6,180", "2.1%"],
            ["gua sha routine", "4,320", "0.8%"],
          ]}
          highlight={0}
        />
        <Panel x={41} y={281} width={558} height={53} tone="green">
          <Text x={20} y={33}>
            Title updated: “A simple gua sha routine for beginners”
          </Text>
        </Panel>
      </>
    );
  },
  "programmatic-seo": () => {
    return (
      <>
        <Panel x={29} y={27} width={582} height={68}>
          <Text x={22} y={29} kind="label">
            PAGE TEMPLATE
          </Text>
          <Text x={22} y={53}>
            /casper/mattresses-for-&#123;sleeper&#125;
          </Text>
          <Chip x={440} y={20} width={121}>
            3 published
          </Chip>
        </Panel>
        {[
          { x: 29, title: "Side sleepers", file: casper },
          { x: 229, title: "Back sleepers", file: casper },
          { x: 429, title: "Hot sleepers", file: casper },
        ].map((page) => (
          <Panel key={page.title} x={page.x} y={119} width={182} height={211}>
            <Text x={15} y={29} kind="label">
              CASPER / MADE FOR
            </Text>
            <Text x={15} y={58} kind="title">
              {page.title}
            </Text>
            <Photo x={38} y={72} width={106} height={112} file={page.file} />
            <Text x={15} y={199} kind="label">
              Comfort · FAQs
            </Text>
          </Panel>
        ))}
        <path d="M320 95v12M120 119v-12h400v12" stroke="#aab38c" fill="none" />
      </>
    );
  },
  "monday-report": () => {
    return (
      <Panel x={39} y={24} width={562} height={310}>
        <Text x={24} y={34} kind="title">
          Monday report
        </Text>
        <Text x={366} y={34} kind="label">
          SEP 21–27 · EXAMPLE
        </Text>
        <Rule y={53} width={514} x={24} />
        <Text x={24} y={85} kind="label">
          REVENUE
        </Text>
        <Text x={205} y={85} kind="label">
          CONVERSION
        </Text>
        <Text x={392} y={85} kind="label">
          ORDERS
        </Text>
        <Text x={24} y={125} kind="metric">
          $24,860
        </Text>
        <Text x={205} y={125} kind="metric">
          3.8%
        </Text>
        <Text x={392} y={125} kind="metric">
          412
        </Text>
        <LineChart x={24} y={151} width={514} height={93} />
        <Text x={24} y={283}>
          Revenue +12.4% · Email drove 31% of sales
        </Text>
      </Panel>
    );
  },
  "competitor-watch": () => {
    return (
      <Panel x={40} y={29} width={560} height={300}>
        <Text x={22} y={36} kind="title">
          Competitor changes
        </Text>
        <Chip x={399} y={17} width={139} tone="neutral">
          Weekly · Sep 28
        </Chip>
        {[
          {
            brand: "AG1",
            before: "Free starter kit",
            after: "Free kit + 5 travel packs",
          },
          { brand: "IM8", before: "$79 / month", after: "$69 / month" },
          {
            brand: "Ritual",
            before: "Product page",
            after: "New comparison page",
          },
        ].map((change, index) => (
          <g key={change.brand} transform={`translate(22 ${77 + index * 72})`}>
            <Rule y={-15} width={516} x={0} />
            <Text kind="label">{change.brand}</Text>
            <Text y={29} fill="#848675">
              {change.before}
            </Text>
            <path d="M215 23h26m-6-6 6 6-6 6" stroke="#70854d" fill="none" />
            <Text x={260} y={29}>
              {change.after}
            </Text>
          </g>
        ))}
      </Panel>
    );
  },
  "review-digest": () => {
    return (
      <Panel x={40} y={28} width={560} height={302}>
        <Text x={22} y={35} kind="title">
          Weekly review digest
        </Text>
        <Chip x={392} y={16} width={145}>
          126 new reviews
        </Chip>
        <Text x={22} y={81}>
          4.8 ★ average
        </Text>
        <Text x={280} y={81} kind="label">
          MOST-MENTIONED THEMES
        </Text>
        {[
          { label: "Flavor", count: 64 },
          { label: "Easy to use", count: 42 },
          { label: "Shipping", count: 20 },
        ].map((theme, index) => (
          <g key={theme.label} transform={`translate(22 ${118 + index * 41})`}>
            <Text y={16}>{theme.label}</Text>
            <rect
              x="155"
              width={theme.count * 4.3}
              height="20"
              rx="5"
              fill={index === 2 ? "#dabaaa" : "#bccf9c"}
            />
            <Text x={474} y={16}>
              {theme.count}
            </Text>
          </g>
        ))}
        <Rule y={245} width={516} />
        <Text x={22} y={278}>
          Watch: shipping mentions rose from 8 to 20
        </Text>
      </Panel>
    );
  },
  "ad-congruence-check": () => {
    return (
      <>
        <Panel x={24} y={43} width={231} height={266}>
          <Text x={18} y={32} kind="label">
            ARMRA / WINNING AD
          </Text>
          <Photo x={18} y={49} width={195} height={136} file={armra} />
          <Text x={18} y={218} kind="title">
            Your first jar, 15% off
          </Text>
          <Text x={18} y={246} kind="label">
            ROAS 3.8× · Running this week
          </Text>
        </Panel>
        <Arrow x={273.5} y={173} />
        <Panel x={322} y={29} width={294} height={295}>
          <Text x={20} y={34} kind="title">
            Landing page check
          </Text>
          <Chip x={20} y={54} tone="rose" width={148}>
            Offer mismatch
          </Chip>
          <Text x={20} y={118}>
            Ad promises 15% off
          </Text>
          <Text x={20} y={150}>
            Page shows full price
          </Text>
          <Rule y={174} width={254} x={20} />
          <Text x={20} y={207} kind="label">
            PROPOSED FIX
          </Text>
          <Text x={20} y={238}>
            Carry FIRST15 into the page
          </Text>
          <Text x={20} y={265}>
            and apply it at checkout
          </Text>
        </Panel>
      </>
    );
  },
  "site-upkeep": () => {
    return (
      <Panel x={40} y={28} width={560} height={303}>
        <Text x={22} y={35} kind="title">
          Friday site check
        </Text>
        <Chip x={397} y={17} width={141} tone="neutral">
          Every Fri · 9 AM
        </Chip>
        <Rule y={58} width={516} />
        {[
          { path: "/summer-sale", issue: "Expired promo", status: "Unpublish" },
          {
            path: "/collections/gifts",
            issue: "Broken link",
            status: "Fix link",
          },
          {
            path: "/products/bestseller",
            issue: "Large image",
            status: "Compress",
          },
        ].map((item, index) => (
          <g key={item.path} transform={`translate(22 ${95 + index * 71})`}>
            <Text>{item.path}</Text>
            <Text y={25} kind="label">
              {item.issue}
            </Text>
            <Chip
              x={390}
              y={-13}
              width={122}
              tone={index === 0 ? "rose" : "green"}
            >
              {item.status}
            </Chip>
          </g>
        ))}
      </Panel>
    );
  },
  "business-website": () => {
    return (
      <>
        <Panel x={22} y={78} width={206} height={204}>
          <Text x={18} y={32} kind="label">
            DESCRIBE YOUR BUSINESS
          </Text>
          <Rule y={46} width={170} x={18} />
          {[
            "We fix leaks and",
            "install water heaters",
            "across Northside.",
            "Open 7 days a week.",
          ].map((line, index) => (
            <Text key={line} x={18} y={78 + index * 26}>
              {line}
            </Text>
          ))}
          <Chip x={18} y={170} width={114} tone="neutral">
            Plumbing
          </Chip>
        </Panel>
        <Arrow x={240} y={180} />
        <Panel x={286} y={26} width={330} height={308}>
          <Text x={20} y={30} kind="label">
            NORTHSIDE PLUMBING
          </Text>
          <Text x={190} y={30} kind="label">
            Services · Contact
          </Text>
          <Rule y={44} x={0} width={330} />
          <Text x={20} y={84} kind="title">
            Same-day plumbing
          </Text>
          <Text x={20} y={110} kind="title">
            in Northside.
          </Text>
          <Text x={20} y={138} kind="label">
            Licensed, insured, and on time.
          </Text>
          <Button x={20} y={154} width={150}>
            Get a quote
          </Button>
          {["Leaks", "Heaters", "Drains"].map((service, index) => (
            <g key={service} transform={`translate(${20 + index * 100} 210)`}>
              <rect
                width="90"
                height="76"
                rx="10"
                className="thumbnail-chip-neutral"
              />
              <circle cx="20" cy="22" r="9" fill="#dce8c4" />
              <Text x={10} y={60} kind="label">
                {service}
              </Text>
            </g>
          ))}
        </Panel>
      </>
    );
  },
  "booking-page": () => {
    return (
      <Panel x={40} y={26} width={560} height={308}>
        <Text x={22} y={34} kind="label">
          HARPER REALTY
        </Text>
        <Text x={22} y={70} kind="title">
          Book a home tour
        </Text>
        <Text x={22} y={96} kind="label">
          30 min · In person or video call
        </Text>
        <Rule y={114} width={516} />
        {["Mon", "Tue", "Wed", "Thu", "Fri"].map((day, index) => (
          <g key={day} transform={`translate(${22 + index * 56} 132)`}>
            <rect
              width="46"
              height="54"
              rx="8"
              fill={index === 3 ? "#273a2c" : "#f1f0e9"}
            />
            <Text
              x={10}
              y={22}
              kind="label"
              fill={index === 3 ? "#fffef8" : undefined}
            >
              {day}
            </Text>
            <Text x={12} y={44} fill={index === 3 ? "#fffef8" : undefined}>
              {14 + index}
            </Text>
          </g>
        ))}
        {["9:00 AM", "10:30 AM", "1:00 PM", "3:30 PM"].map((time, index) => (
          <Chip
            key={time}
            x={22 + (index % 2) * 132}
            y={206 + Math.floor(index / 2) * 40}
            width={120}
            tone={index === 1 ? "green" : "neutral"}
          >
            {time}
          </Chip>
        ))}
        <Panel x={318} y={132} width={220} height={154} tone="green">
          <Text x={18} y={32} kind="label">
            YOUR TOUR
          </Text>
          <Text x={18} y={64}>
            Thursday, Oct 17
          </Text>
          <Text x={18} y={88}>
            10:30 AM
          </Text>
          <Button x={18} y={106} width={184}>
            Confirm booking
          </Button>
        </Panel>
      </Panel>
    );
  },
  "services-pricing-page": () => {
    return (
      <>
        <Text x={40} y={46} kind="label">
          BRIGHT HOME CLEANING · SERVICES & PRICES
        </Text>
        <PlanCards
          badge="Most booked"
          unit="per visit"
          plans={[
            ["Standard", "$120", "Kitchen, baths, floors", "Book"],
            ["Deep clean", "$220", "Adds appliances", "Book"],
            ["Move-out", "$320", "Whole empty home", "Book"],
          ]}
        />
      </>
    );
  },
  "offer-landing-page": () => {
    return (
      <>
        <Panel x={22} y={60} width={196} height={245}>
          <Text x={16} y={28} kind="label">
            RIDGELINE · Sponsored
          </Text>
          <rect x="16" y="42" width="164" height="118" rx="12" fill="#dce8c4" />
          <Text x={34} y={98} kind="title">
            First week
          </Text>
          <Text x={34} y={124} kind="title">
            on us.
          </Text>
          <Text x={16} y={190}>
            New members only.
          </Text>
          <Text x={16} y={214} kind="label">
            Learn more
          </Text>
        </Panel>
        <Arrow x={233} y={176} />
        <Panel x={280} y={26} width={336} height={308}>
          <Text x={20} y={30} kind="label">
            RIDGELINE GYM
          </Text>
          <Rule y={44} x={0} width={336} />
          <Text x={20} y={84} kind="title">
            Your first week is free.
          </Text>
          <Text x={20} y={110} kind="label">
            Classes, open gym, and a coach on day one.
          </Text>
          {["Your name", "Phone number"].map((field, index) => (
            <g key={field} transform={`translate(20 ${130 + index * 50})`}>
              <rect
                width="296"
                height="38"
                rx="8"
                fill="#fffef8"
                stroke="#d9dbce"
              />
              <Text x={14} y={24} kind="label">
                {field}
              </Text>
            </g>
          ))}
          <Button x={20} y={236} width={296}>
            Claim my free week
          </Button>
          <Text x={20} y={296} kind="label">
            No card needed · Cancel anytime
          </Text>
        </Panel>
      </>
    );
  },
  "saas-landing-page": () => {
    return (
      <Panel x={40} y={26} width={560} height={308}>
        <Text x={24} y={32} kind="label">
          LEDGERLY
        </Text>
        <Text x={300} y={32} kind="label">
          Product · Pricing · Log in
        </Text>
        <Rule y={48} x={0} width={560} />
        <Text x={24} y={94} kind="title">
          Close your books in one day,
        </Text>
        <Text x={24} y={120} kind="title">
          not ten.
        </Text>
        <Text x={24} y={150} kind="label">
          Bank sync, matching, and reports for small finance teams.
        </Text>
        <g transform="translate(24 168)">
          <rect
            width="230"
            height="38"
            rx="8"
            fill="#fffef8"
            stroke="#d9dbce"
          />
          <Text x={14} y={24} kind="label">
            Work email
          </Text>
        </g>
        <Button x={266} y={170} width={150}>
          Start free trial
        </Button>
        <Rule y={232} width={512} x={24} />
        <Text x={24} y={258} kind="label">
          Trusted by 2,000 finance teams
        </Text>
        {[0, 1, 2, 3, 4].map((index) => (
          <rect
            key={index}
            x={24 + index * 104}
            y={272}
            width="88"
            height="18"
            rx="9"
            className="thumbnail-chip-neutral"
          />
        ))}
      </Panel>
    );
  },
  "saas-feature-page": () => {
    return (
      <>
        <Panel x={24} y={26} width={300} height={308}>
          <Text x={20} y={32} kind="label">
            LEDGERLY / FEATURES
          </Text>
          <Text x={20} y={74} kind="title">
            Bank sync that
          </Text>
          <Text x={20} y={100} kind="title">
            matches itself.
          </Text>
          {[
            "Connects to 9,000 banks",
            "Matches payments to invoices",
            "Flags what needs a look",
          ].map((line, index) => (
            <g key={line} transform={`translate(20 ${136 + index * 40})`}>
              <circle cx="10" cy="-5" r="10" fill="#dce8c4" />
              <path
                d="M5 -5l4 4 6-8"
                fill="none"
                stroke="#628344"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Text x={30}>{line}</Text>
            </g>
          ))}
          <Button x={20} y={256} width={160}>
            Try it free
          </Button>
        </Panel>
        <Panel x={344} y={58} width={272} height={244} tone="green">
          <Text x={18} y={32} kind="label">
            BANK FEED · TODAY
          </Text>
          <Rule y={46} x={18} width={236} />
          {[
            { name: "Acme Corp", amount: "$4,200", matched: true },
            { name: "Stripe payout", amount: "$1,860", matched: true },
            { name: "AWS", amount: "$312", matched: true },
            { name: "Unknown", amount: "$95", matched: false },
          ].map((row, index) => (
            <g key={row.name} transform={`translate(18 ${74 + index * 42})`}>
              <Text>{row.name}</Text>
              <Text x={110}>{row.amount}</Text>
              <Chip
                x={168}
                y={-19}
                width={68}
                tone={row.matched ? "green" : "rose"}
              >
                {row.matched ? "Matched" : "Review"}
              </Chip>
            </g>
          ))}
        </Panel>
      </>
    );
  },
  "saas-pricing-page": () => {
    return (
      <>
        <Text x={40} y={42} kind="label">
          LEDGERLY · PRICING
        </Text>
        <g transform="translate(424 22)">
          <rect
            width="176"
            height="30"
            rx="15"
            className="thumbnail-chip-neutral"
          />
          <rect x="88" y="3" width="85" height="24" rx="12" fill="#fffef8" />
          <Text x={18} y={20} kind="label">
            Monthly
          </Text>
          <Text x={104} y={20} kind="label">
            Yearly
          </Text>
        </g>
        <PlanCards
          badge="Most popular"
          unit="per user / month"
          plans={[
            ["Starter", "$0", "1 bank account", "Start free"],
            ["Team", "$29", "Unlimited accounts", "Choose plan"],
            ["Business", "$79", "Approvals and audit log", "Choose plan"],
          ]}
        />
      </>
    );
  },
  "store-from-scratch": () => {
    return (
      <>
        <Panel x={16} y={72} width={176} height={216}>
          <Text x={16} y={30} kind="label">
            1 · PICK A PRODUCT
          </Text>
          <Photo x={16} y={44} width={144} height={110} file={coffeeBox} />
          <Text x={16} y={180}>
            Coffee box
          </Text>
          <Text x={16} y={202} kind="label">
            $24 · In stock
          </Text>
        </Panel>
        <Arrow x={200} y={180} />
        <Panel x={240} y={72} width={160} height={216}>
          <Text x={16} y={30} kind="label">
            2 · TEMPLATE
          </Text>
          {[0, 1, 2, 3].map((index) => (
            <g
              key={index}
              transform={`translate(${16 + (index % 2) * 68} ${44 + Math.floor(index / 2) * 82})`}
            >
              <rect
                width="60"
                height="72"
                rx="8"
                fill={index === 1 ? "#dce8c4" : "#f1f0e9"}
                stroke={index === 1 ? "#6b8650" : "#d9dbce"}
              />
              <rect x="8" y="10" width="44" height="26" rx="4" fill="#fffef8" />
              <rect
                x="8"
                y="44"
                width="30"
                height="5"
                rx="2.5"
                fill="#c9cdbb"
              />
              <rect x="8" y="55" width="22" height="8" rx="4" fill="#273a2c" />
            </g>
          ))}
        </Panel>
        <Arrow x={408} y={180} />
        <Page
          x={448}
          y={40}
          width={176}
          height={280}
          brand="YOURSTORE.COM"
          headline="Morning, sorted."
          file={coffeeBox}
          action="Add to cart"
          tone="green"
        />
      </>
    );
  },
} satisfies Record<UseCaseArtworkId, () => ReactNode>;

export function UseCaseWorkflowArt({ slug }: { slug: UseCaseArtworkId }) {
  const Scene = WORKFLOW_SCENES[slug];
  return (
    <svg
      viewBox="0 0 640 360"
      className="use-case-workflow-art"
      aria-hidden="true"
      data-use-case={slug}
      data-workflow={USE_CASE_ARTWORK[slug].workflow}
    >
      <g className="use-case-scene">
        <Scene />
      </g>
    </svg>
  );
}
